import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import closeIcon from 'assets/svg/close-icon.svg';
import closeImage from 'assets/svg/close.svg';
import adminMessageIcon from 'assets/svg/admin-message-icon.svg';
import Styles from 'components/chatContent/messageThread/MessageThread.module.scss';
import ChatFooter from 'components/chatContent/chatFooter/ChatFooter';
import MessageBox from 'components/chatContent/messageBox/MessageBox';
import ProfileAvatar from 'components/profileAvatar/ProfileAvatar';
import { getImages, useGetThreadMessages } from 'store/services/firebase-broadcast';
import {
  formatMessageWithLineBreaks,
  getAttachmentMediaType,
  getMessageDateLabel,
  getMessageTime,
  findFirstUnreadMessageId,
} from 'helpers/utils';
import { useRoomTypers } from 'store/services/typingPresence';
import { markThreadRead, useThreadReads } from 'store/services/threadReads';
import {
  ensureThreadNewDividerAnchor,
  dismissThreadNewDivider,
  getThreadNewDividerAnchor,
} from 'store/services/newMessagesDividerStore';
import TypingLabel from 'components/chatContent/typingLabel/TypingLabel';
import NewMessagesDivider from 'components/chatContent/newMessagesDivider/NewMessagesDivider';
import { useDateTimeFormat } from 'hooks/useDateTimeFormat';

interface MessageThreadProps {
  hideReplies: Function;
  item: any;
  selected: any;
  actions: boolean;
  isDrawerOpen?: boolean;
}

const MessageThread: FC<MessageThreadProps> = (props: MessageThreadProps) => {
  const { hideReplies, item, selected, isDrawerOpen } = props;
  const [attachment, setAttachment] = useState('');
  const [images, setImages] = useState<any[]>([]);
  /** Phone: measured ChatFooter height for typing bar + scroll clearance. */
  const [composerHeight, setComposerHeight] = useState(64);
  const onComposerHeightChange = useCallback((height: number) => {
    setComposerHeight((prev) => (prev === height ? prev : height));
  }, []);
  const composerHeightStyle = useMemo(
    () => ({ ['--composer-height' as string]: `${composerHeight}px` }) as React.CSSProperties,
    [composerHeight]
  );
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const handleShowReplies = () => hideReplies(false);
  const { formatTime, setting: dateTimeSetting } = useDateTimeFormat();

  const { threads, isLoading } = useGetThreadMessages(selected?.pk, item?.id);
  const typers = useRoomTypers(selected?.pk, user?.pk, item?.id);
  const hasTypers = typers.length > 0;
  const prevHasTypersRef = useRef(false);
  const { reads: threadReads, isReady: threadReadsReady } = useThreadReads(
    selected?.pk,
    user?.pk
  );
  const [openThreadLastSeenTime, setOpenThreadLastSeenTime] = useState<number | null>(null);
  const [threadSnapshotReady, setThreadSnapshotReady] = useState(false);
  const [threadDividerDismissed, setThreadDividerDismissed] = useState(false);

  // Group replies by calendar day — same idea as ChatContent, but labels stay inline
  // (not sticky) so they don't fight the sticky parent card in this narrow column.
  const dayGroups = useMemo(() => {
    const groups: { key: string; label: string; items: { reply: any; index: number }[] }[] = [];
    (threads || []).forEach((reply: any, index: number) => {
      const ts = getMessageTime(reply);
      if (!ts) {
        if (groups.length) groups[groups.length - 1].items.push({ reply, index });
        else groups.push({ key: 'undated', label: '', items: [{ reply, index }] });
        return;
      }
      const key = new Date(ts).toDateString();
      const last = groups[groups.length - 1];
      if (!last || last.key !== key) {
        groups.push({
          key,
          label: getMessageDateLabel(ts, { compact: true, setting: dateTimeSetting }),
          items: [{ reply, index }],
        });
      } else {
        last.items.push({ reply, index });
      }
    });
    return groups;
  }, [threads, dateTimeSetting]);

  const firstUnreadReplyId = useMemo(
    () =>
      threadSnapshotReady && !threadDividerDismissed
        ? findFirstUnreadMessageId(threads, openThreadLastSeenTime, user?.pk)
        : null,
    [threads, openThreadLastSeenTime, user?.pk, threadSnapshotReady, threadDividerDismissed]
  );

  const dismissThreadDivider = useCallback(() => {
    if (!selected?.pk || !item?.id) return;
    const newest = threads?.length ? threads[threads.length - 1] : null;
    const newestTime = newest ? getMessageTime(newest) : null;
    dismissThreadNewDivider(selected.pk, item.id, newestTime);
    if (newestTime != null) setOpenThreadLastSeenTime(newestTime);
    setThreadDividerDismissed(true);
  }, [selected?.pk, item?.id, threads]);

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const setBodyRef = useCallback((node: HTMLDivElement | null) => {
    bodyRef.current = node;
    setScrollNode(node);
  }, []);

  const nearBottomRef = useRef(true);
  const isInitialLoadRef = useRef(true);
  const prevThreadsLenRef = useRef(0);
  const lastMarkedThreadRef = useRef('');
  const [showNewRepliesPill, setShowNewRepliesPill] = useState(false);

  const getTime = (time: string) => {
    if (!time) return '';
    return formatTime(time);
  };

  // Opening / catching up in a thread marks its replies read (Slack-style).
  const persistThreadRead = useCallback(() => {
    if (!selected?.pk || !user?.pk || !item?.id || isLoading) return;
    const markKey = `${selected.pk}:${item.id}:${threads.length}`;
    if (lastMarkedThreadRef.current === markKey) return;
    lastMarkedThreadRef.current = markKey;
    markThreadRead(selected.pk, user.pk, item.id, threads).catch((error) => {
      console.warn('Failed to mark thread read:', error);
      lastMarkedThreadRef.current = '';
    });
  }, [selected?.pk, user?.pk, item?.id, threads, isLoading]);

  useEffect(() => {
    // Reset guard when switching parent message.
    lastMarkedThreadRef.current = '';
    isInitialLoadRef.current = true;
    prevThreadsLenRef.current = 0;
    nearBottomRef.current = true;
    setShowNewRepliesPill(false);
    setOpenThreadLastSeenTime(null);
    setThreadSnapshotReady(false);
    setThreadDividerDismissed(false);
    prevHasTypersRef.current = false;
  }, [item?.id, selected?.pk]);

  // Freeze lastSeen before persistThreadRead; dismiss advances cursor across reopen/refresh.
  useLayoutEffect(() => {
    if (!threadReadsReady || !item?.id || !selected?.pk || threadSnapshotReady) return;

    const cached = getThreadNewDividerAnchor(selected.pk, item.id);
    if (cached && !cached.dismissed) {
      setOpenThreadLastSeenTime(cached.lastSeenTime);
      setThreadDividerDismissed(false);
      setThreadSnapshotReady(true);
      return;
    }

    const read = threadReads[item.id];
    const lastSeen =
      read && typeof read.lastSeenTime === 'number' ? read.lastSeenTime : 0;
    const anchor = ensureThreadNewDividerAnchor(selected.pk, item.id, lastSeen);
    setOpenThreadLastSeenTime(anchor.lastSeenTime);
    setThreadDividerDismissed(anchor.dismissed);
    setThreadSnapshotReady(true);
  }, [threadReadsReady, threadReads, item?.id, selected?.pk, threadSnapshotReady]);

  useEffect(() => {
    if (isLoading || !threadSnapshotReady) return;
    persistThreadRead();
  }, [isLoading, threads.length, persistThreadRead, threadSnapshotReady]);

  useEffect(() => {
    setAttachment('');
    if (item.attachments?.length > 0 && item.attachments[0]?.id) {
      getImages(selected.pk, item.attachments[0].id)
        .then((e) => setAttachment(e[0]))
        .catch((error) => {
          console.warn('Failed to load thread attachment:', error);
        });
    }
  }, [item.attachments, item?.id, selected?.pk]);

  // Track whether the user is following the latest replies.
  useEffect(() => {
    if (!scrollNode) return undefined;

    const handleScroll = () => {
      const distanceFromBottom =
        scrollNode.scrollHeight - scrollNode.scrollTop - scrollNode.clientHeight;
      nearBottomRef.current = distanceFromBottom < 120;
      if (nearBottomRef.current) {
        setShowNewRepliesPill(false);
        persistThreadRead();
      }
    };

    scrollNode.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => scrollNode.removeEventListener('scroll', handleScroll);
  }, [scrollNode, persistThreadRead]);

  const scrollToBottom = useCallback(() => {
    const node = bodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
    nearBottomRef.current = true;
    setShowNewRepliesPill(false);
  }, []);

  // Receiver-only: when a remote typer appears, the typing band shrinks/overlays the
  // list. Re-anchor after layout if the user was already following the latest.
  useLayoutEffect(() => {
    const appeared = hasTypers && !prevHasTypersRef.current;
    prevHasTypersRef.current = hasTypers;
    if (!scrollNode || !appeared || !nearBottomRef.current) return undefined;

    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled || !scrollNode) return;
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ block: 'end', inline: 'nearest' });
        }
        scrollNode.scrollTop = scrollNode.scrollHeight;
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [hasTypers, scrollNode]);

  const parentHasAttachment = Boolean(item?.attachments?.[0]?.id);
  // Don't finish the initial scroll until the parent media URL has resolved (or
  // there is none) — otherwise the image load pushes the last reply out of view.
  const attachmentReady = !parentHasAttachment || Boolean(attachment);

  // Initial open: scroll to NEW divider when there are unread replies, else bottom.
  useLayoutEffect(() => {
    if (!scrollNode || isLoading || !attachmentReady || !isInitialLoadRef.current) {
      return undefined;
    }
    // Wait for unread snapshot so we don't jump to bottom then snap up.
    if (!threadSnapshotReady) return undefined;

    const container = scrollNode;

    const scrollToInitialTarget = () => {
      if (firstUnreadReplyId) {
        const divider = container.querySelector(
          '[data-new-messages-divider]'
        ) as HTMLElement | null;
        if (divider) {
          const sticky = container.querySelector(
            '[data-thread-sticky]'
          ) as HTMLElement | null;
          const stickyH = sticky?.offsetHeight ?? 0;
          const top =
            divider.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop;
          container.scrollTop = Math.max(0, top - stickyH - 8);
          nearBottomRef.current = false;
          return;
        }
      }
      container.scrollTop = container.scrollHeight;
      nearBottomRef.current = true;
    };

    scrollToInitialTarget();

    let lastHeight = container.scrollHeight;
    let stableCount = 0;
    const requiredStable = 2;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      scrollToInitialTarget();
      isInitialLoadRef.current = false;
      prevThreadsLenRef.current = threads.length;
    };

    const onHeightCheck = () => {
      if (finished || !isInitialLoadRef.current) return;
      const currentHeight = container.scrollHeight;
      scrollToInitialTarget();
      if (currentHeight === lastHeight) {
        stableCount += 1;
        if (stableCount >= requiredStable) finish();
      } else {
        stableCount = 0;
        lastHeight = currentHeight;
      }
    };

    const resizeObserver = new ResizeObserver(onHeightCheck);
    resizeObserver.observe(container);
    Array.from(container.children).forEach((child) => resizeObserver.observe(child));

    const intervalId = setInterval(onHeightCheck, 100);

    const fallbackTimeout = setTimeout(() => {
      finish();
    }, 2000);

    return () => {
      resizeObserver.disconnect();
      clearInterval(intervalId);
      clearTimeout(fallbackTimeout);
    };
  }, [
    scrollNode,
    isLoading,
    attachmentReady,
    threads.length,
    attachment,
    item?.id,
    threadSnapshotReady,
    firstUnreadReplyId,
  ]);

  // Follow new replies when at/near bottom; otherwise show "New replies".
  useEffect(() => {
    const prevLen = prevThreadsLenRef.current;
    prevThreadsLenRef.current = threads.length;

    if (!scrollNode || threads.length <= prevLen) return;
    if (isInitialLoadRef.current) return;

    if (nearBottomRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
      persistThreadRead();
    } else {
      setShowNewRepliesPill(true);
    }
  }, [threads.length, scrollNode, scrollToBottom, persistThreadRead]);

  const scrollToBottomAndMarkRead = useCallback(() => {
    scrollToBottom();
    persistThreadRead();
    setShowNewRepliesPill(false);
  }, [scrollToBottom, persistThreadRead]);

  const handleImages = (newImage: any) => {
    setImages([...images, newImage]);
  };

  const clearImages = () => {
    setImages([]);
  };

  const removeImage = (imageId: string) => {
    setImages(images.filter((img) => img.id !== imageId));
  };

  const onMessageSent = () => {
    requestAnimationFrame(() => {
      scrollToBottom();
      persistThreadRead();
    });
  };

  const isRoomHost =
    item.sender.isHost || item.sender.id === selected?.host?.pk;
  const isSuperHost =
    isRoomHost &&
    (item.sender.isSuperHost ?? selected?.host?.is_super_host);

  return (
    <div className={`${Styles.messageThreadContainer} ${isDrawerOpen ? Styles.drawerOpen : ''}`}>
      <div className={Styles.messageThreadWrapper} style={composerHeightStyle}>
        <div className={Styles.header}>
          <div className={Styles.headerTitle}>
            <img src={adminMessageIcon} alt="" className={Styles.headerIcon} aria-hidden="true" />
            <h6 className={Styles.headerLabel}>MESSAGE THREAD</h6>
          </div>
          <div
            className={Styles.closeButton}
            onClick={handleShowReplies}
            onKeyPress={handleShowReplies}
            role="button"
            tabIndex={0}
            aria-label="Close message thread"
          >
            <img src={closeIcon} alt="" className={Styles.closeIcon} />
          </div>
        </div>
        <div
          ref={setBodyRef}
          className={`customScrollBar ${Styles.BodyContent} ${
            hasTypers ? Styles.hasTypingClearance : ''
          }`}
        >
          <div className={Styles.stickyParent} data-thread-sticky>
            <div className={`mb-0 ${Styles.message}`}>
              <ProfileAvatar profile={item.sender.image} alt="user" className={Styles.userImage} />
              <div className={Styles.userDetails}>
                <div className={Styles.detailsInner}>
                  <h5 className="my-0">{item.sender.name}</h5>
                  <span className={Styles.messageTime}>
                    {getTime(new Date(getMessageTime(item)).toISOString())}
                  </span>
                  {isSuperHost && (
                    <span className={`badge text-white ${Styles.superHostBadge}`}>SUPER HOST</span>
                  )}
                  {isRoomHost && !isSuperHost && (
                    <span className={`badge text-white ${Styles.hostBadge}`}>Host</span>
                  )}
                </div>
                <p
                  className={Styles.messageText}
                  dangerouslySetInnerHTML={{ __html: formatMessageWithLineBreaks(item.message) }}
                />
                {attachment && (
                  <div className="d-flex flex-column">
                    <span className={Styles.uploadedImageDetails}>{item.attachments[0].name}</span>
                    {getAttachmentMediaType(item.attachments[0]) === 'video' ? (
                      <video className={Styles.uploadedImage} controls src={attachment} />
                    ) : (
                      <img src={attachment} alt="attachment" className={Styles.uploadedImage} />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={Styles.threadSeparator} aria-hidden="true" />
            <p className={Styles.viewRepliesLabel}>VIEW REPLIES ({threads.length})</p>
          </div>
          {dayGroups.map((group) => (
            <div key={group.key} className={Styles.daySection}>
              {group.label && (
                <div className={Styles.threadDateSeparator}>
                  <span>{group.label}</span>
                </div>
              )}
              {group.items.map(({ reply, index }) => (
                <React.Fragment
                  key={reply.id || `${reply.timestamp || reply.createdTime}-${reply.sender?.pk || index}`}
                >
                  {firstUnreadReplyId &&
                    String(reply.id) === firstUnreadReplyId && <NewMessagesDivider />}
                  <MessageBox
                    item={reply}
                    type="threads"
                    actions={false}
                    selected={selected}
                    isThread
                    parentMessageId={item.id}
                    threadIndex={index}
                  />
                </React.Fragment>
              ))}
            </div>
          ))}
          <div ref={bottomRef} aria-hidden="true" />
        </div>

        {showNewRepliesPill && (
          <button
            type="button"
            className={`${Styles.newRepliesPill} ${
              hasTypers ? Styles.newRepliesPillAboveTyping : ''
            }`}
            onClick={scrollToBottomAndMarkRead}
          >
            New replies ↓
          </button>
        )}

        {images.length > 0 && (
          <div className={Styles.attachedMedia}>
            {images.map((i: any, index: number) => (
              <div
                className={`position-relative ${Styles.attachedInnerMedia}`}
                key={i.id || `image-${index}`}
              >
                {i.type === 'video' ? (
                  <video
                    width="320"
                    height="240"
                    className={Styles.attachedFile}
                    controls
                  >
                    <source src={i.path} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={i.path}
                    className={Styles.attachedFile}
                    alt="attached-file"
                  />
                )}
                <div
                  className={Styles.closeIconWrapper}
                  onClick={() => removeImage(i.id)}
                >
                  <img src={closeImage} alt="close-icon" />
                </div>
              </div>
            ))}
          </div>
        )}

        <TypingLabel typers={typers} />
        <ChatFooter
          selected={selected}
          isThread
          parentMessage={item}
          images={handleImages}
          clearImages={clearImages}
          attachedImages={images}
          onMessageSent={onMessageSent}
          onComposerInteract={dismissThreadDivider}
          onHeightChange={onComposerHeightChange}
        />
      </div>
    </div>
  );
};
export default MessageThread;
